import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './AdminTools.css';

/**
 * Verktyg & Test — owner-only hub that gathers the project's dev/test tools
 * that previously lived scattered across other admin sections:
 *  - Demo-match-skapare (flyttat från Inställningar → Funktioner)
 *  - Fyll kön till 10 (flyttat från Kö)
 *
 * Voice "Testa anslutning" stannar medvetet kvar i Inställningar → Voice
 * eftersom den validerar den config man precis fyllt i.
 */
export default function AdminTools({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  // --- Demo-match state ---
  const [demoState, setDemoState] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoError, setDemoError] = useState('');

  // --- Queue-fill state ---
  const [fillLoading, setFillLoading] = useState(false);
  const [fillResult, setFillResult] = useState('');
  const [fillError, setFillError] = useState('');

  const fetchDemoStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/demo/status`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (r.ok) setDemoState(await r.json());
    } catch {
      // silent
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchDemoStatus();
  }, [fetchDemoStatus]);

  const createDemoMatch = async (matchType) => {
    setDemoBusy(true);
    setDemoError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/demo/create-match`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ match_type: matchType }),
      });
      const data = await r.json();
      if (!r.ok) {
        setDemoError(data.error || data.detail || t('admin.createDemoFailed'));
        return;
      }
      navigate(`/lobby/${data.match_id}`);
    } catch {
      setDemoError(t('settings.networkError'));
    } finally {
      setDemoBusy(false);
    }
  };

  const cleanupDemo = async () => {
    if (!window.confirm(t('admin.confirmCleanupDemo'))) return;
    setDemoBusy(true);
    setDemoError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/demo/cleanup`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        await fetchDemoStatus();
      } else {
        const data = await r.json().catch(() => ({}));
        setDemoError(data.error || data.detail || t('admin.cleanupFailed'));
      }
    } catch {
      setDemoError(t('settings.networkError'));
    } finally {
      setDemoBusy(false);
    }
  };

  const handleFill = async () => {
    setFillLoading(true);
    setFillError('');
    setFillResult('');
    try {
      const r = await fetch(`${API_URL}/api/admin/queue/${perms.guildId}/fill`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: 10 }),
      });
      const data = await r.json();
      if (!r.ok) {
        setFillError(data.error || data.detail || t('admin.fillQueueFailed'));
      } else {
        setFillResult(
          t('admin.fillResult', { added: data.added, size: data.queue_size, target: data.target })
        );
      }
    } catch {
      setFillError(t('settings.networkError'));
    } finally {
      setFillLoading(false);
    }
  };

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.navTools')}</h1>
      <div className="admin-card-desc admin-tools-intro">
        {t('admin.toolsIntro')}
      </div>

      <div className="admin-card">
        <div className="admin-card-title">{t('admin.demoMatchTitle')}</div>
        <div className="admin-card-desc">
          {t('admin.demoMatchDesc')}
        </div>
        <div className="admin-row admin-tools-actions">
          <button className="admin-btn" onClick={() => createDemoMatch('BO1')} disabled={demoBusy}>
            {t('admin.createBo', { type: 'BO1' })}
          </button>
          <button className="admin-btn" onClick={() => createDemoMatch('BO3')} disabled={demoBusy}>
            {t('admin.createBo', { type: 'BO3' })}
          </button>
          <button className="admin-btn" onClick={() => createDemoMatch('BO5')} disabled={demoBusy}>
            {t('admin.createBo', { type: 'BO5' })}
          </button>
          <button
            className="admin-btn admin-btn-danger"
            onClick={cleanupDemo}
            disabled={demoBusy || !demoState?.fixtures_exist}
          >
            {t('admin.clearDemoData')}
          </button>
        </div>
        {demoState && (
          <div className="admin-info admin-tools-meta">
            {demoState.fixtures_exist
              ? t('admin.activeDemoMatches', { count: demoState.active_matches, guild: demoState.guild_id })
              : t('admin.noDemoFixtures')}
          </div>
        )}
        {demoError && (
          <div className="admin-row">
            <span className="admin-error">{demoError}</span>
          </div>
        )}
      </div>

      <div className="admin-card">
        <div className="admin-card-title">{t('admin.fillQueueTitle')}</div>
        <div className="admin-card-desc">
          {t('admin.fillQueueDesc')}
        </div>
        <div className="admin-row admin-tools-actions">
          <button
            className="admin-btn admin-btn-secondary"
            onClick={handleFill}
            disabled={fillLoading || !perms.guildId}
          >
            {fillLoading ? t('admin.filling') : t('admin.fillQueueTo10')}
          </button>
        </div>
        {fillResult && (
          <div className="admin-info admin-tools-result">{fillResult}</div>
        )}
        {fillError && (
          <div className="admin-row">
            <span className="admin-error">{fillError}</span>
          </div>
        )}
      </div>
    </div>
  );
}
